import React, { PureComponent } from 'react';
import { observer } from 'mobx-react';
import { FaRegBookmark } from 'react-icons/fa';

import { SalaryMonthViewModelResponseExt, RuleGroupItem, SalaryItemUnit } from '@Models';
import { SizeConfig, DataGrid, DataFieldType, ToolTipItem, DataGridColumnDef, CrossTableHoverStore } from '@Components';
import './salary-table.scss';

type SalaryTableProps = {
    dataSource: SalaryMonthViewModelResponseExt[];
    fullDataSource?: SalaryMonthViewModelResponseExt[];
    filteredItems?: SalaryMonthViewModelResponseExt[];
    ruleGroups: RuleGroupItem[];
    classPreffix?: string;
    selectedRow?: SalaryMonthViewModelResponseExt;
    crossTableHoverStore?: CrossTableHoverStore;
    totalRow?: boolean;
    scrollHandler?: (top: number) => void;
    onCellDoubleClick: (item: SalaryMonthViewModelResponseExt, rg: RuleGroupItem) => void;
    onRowClick: (row: SalaryMonthViewModelResponseExt) => void;
    onResizeHeader?: (width?: number, height?: number) => void;
    sizeConfig?: SizeConfig;
    loading?: boolean;
    scrollTop?: number;
    onScroll?: (scrollTop: number) => void;
    headerHeight?: number;
    onHeaderHeightChange?: (height: number) => void;
    onFilteredItemsChange?: (items: SalaryMonthViewModelResponseExt[]) => void;
    onOwnFilteredItemsChange?: (items: SalaryMonthViewModelResponseExt[]) => void;
    onColumnFiltersActiveChange?: (isActive: boolean) => void;
    onHorizontalScrollbarHeightChange?: (height: number) => void;
};

@observer
class SalaryTable extends PureComponent<SalaryTableProps, {}> {
    private _dataGridRef = React.createRef<DataGrid<SalaryMonthViewModelResponseExt>>();

    public scrollTo(top: number) {
        this._dataGridRef.current?.scrollTo(top);
    }

    render() {
        const { dataSource, fullDataSource, totalRow, sizeConfig, ruleGroups, loading, scrollTop, onScroll,
            headerHeight, onHeaderHeightChange, selectedRow, onFilteredItemsChange, onOwnFilteredItemsChange,
            onColumnFiltersActiveChange, onHorizontalScrollbarHeightChange } = this.props;
        const maxHeight = sizeConfig?.height != null ? `${sizeConfig.height}px` : undefined;
        const totalRowContent = totalRow && ruleGroups.length ? this._computeTotalRow() : undefined;

        return (
            <DataGrid<SalaryMonthViewModelResponseExt>
                ref={this._dataGridRef}
                tableKey="salary-table"
                idField="personId"
                columns={this._columns}
                items={dataSource}
                initialItemsList={fullDataSource ?? dataSource}
                filteredItemsWithoutColumnFilters={dataSource}
                maxHeight={maxHeight}
                totalRow={totalRowContent}
                onRowClick={this.props.onRowClick}
                scrollTop={scrollTop}
                onScroll={onScroll}
                headerHeight={headerHeight}
                onHeaderHeightChange={onHeaderHeightChange}
                selectedRowId={selectedRow?.personId}
                crossTableHoverStore={this.props.crossTableHoverStore}
                onFilteredItemsChange={onFilteredItemsChange}
                onOwnFilteredItemsChange={onOwnFilteredItemsChange}
                onColumnFiltersActiveChange={onColumnFiltersActiveChange}
                onHorizontalScrollbarHeightChange={onHorizontalScrollbarHeightChange}
                loading={loading}
            />
        );
    }

    private get _columns(): DataGridColumnDef<SalaryMonthViewModelResponseExt>[] {
        return this.props.ruleGroups.map(rg => ({
            title: rg.importType ?? rg.name ?? '',
            dataField: `ruleGroupId_${rg.id}` as keyof SalaryMonthViewModelResponseExt,
            dataFieldType: DataFieldType.Number,
            renderHeader: () => <>{rg.importType ?? rg.name}</>,
            renderSubTitle: () => <small>{rg.sapAccount}, {rg.unit === SalaryItemUnit.Money ? 'CHF' : rg.unit}</small>,
            getFilterOptionName: (item) => {
                const val = item[`ruleGroupId_${rg.id}`];
                return typeof val === 'number' ? val.toFixed(2) : '';
            },
            renderCell: (_value, item) => this._renderCell(rg, item)
        }));
    }

    private _renderCell(rg: RuleGroupItem, item: SalaryMonthViewModelResponseExt) {
        const currentGroup = item.groups?.find(
            g => g.ruleGroupId === rg.id && g.sapAccount === rg.sapAccount && g.unit === rg.unit
        );
        if (!currentGroup) return null;

        const cellId = 'c' + item.personCode + '_' + rg.id;

        return (
            <div onDoubleClick={() => this.props.onCellDoubleClick(item, rg)}>
                {currentGroup.hasCorrections && <>
                    <FaRegBookmark className="correction-icon" id={cellId} />
                    <ToolTipItem text="Value has some corrections, go to daily view for more details" placement="top" targetId={cellId} />
                </>}
                {currentGroup.amount?.toFixed(2)}
            </div>
        );
    }

    private _computeTotalRow(): React.ReactNode[] {
        const { ruleGroups } = this.props;
        const items = this.props.filteredItems ?? this.props.dataSource;
        return ruleGroups.map(rg => {
            let total = 0;
            for (const item of items) {
                const g = item.groups?.find(
                    g => g.ruleGroupId === rg.id && g.sapAccount === rg.sapAccount
                );
                if (typeof g?.amount === 'number') total += g.amount;
            }
            return <React.Fragment key={rg.id}>{total ? total.toFixed(2) : '-'}</React.Fragment>;
        });
    }
}

export { SalaryTable };
